'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { LocaleSwitcher } from '@/components/landing/LocaleSwitcher'
import { Button } from '@/components/ui/button'

export interface NavLink {
  href: string
  label: string
}

interface BookingHeaderActionsProps {
  navLinks?: NavLink[]
  bookNowLabel?: string
  manageReservationLabel?: string
}

/**
 * Client component for booking header interactive elements
 * Includes locale switcher, theme toggle, and mobile hamburger menu.
 */
export function BookingHeaderActions({ navLinks = [], bookNowLabel, manageReservationLabel }: BookingHeaderActionsProps) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <>
      <div className="flex items-center gap-2">
        <LocaleSwitcher />
        <ThemeToggle />
        {/* Hamburger button — visible below lg */}
        {navLinks.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        )}
      </div>

      {/* Mobile dropdown menu */}
      {mobileOpen && navLinks.length > 0 && (
        <div className="absolute top-16 inset-x-0 bg-background/95 backdrop-blur-xl border-b border-border shadow-lg lg:hidden z-50">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {manageReservationLabel && (
              <Link
                href="/manage-reservation"
                onClick={() => setMobileOpen(false)}
                className="mt-1 flex items-center justify-center px-4 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground border border-border hover:border-foreground/20 rounded-lg transition-colors"
              >
                {manageReservationLabel}
              </Link>
            )}
            {bookNowLabel && (
              <Link
                href="/#search"
                onClick={() => setMobileOpen(false)}
                className="mt-1 flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors"
              >
                {bookNowLabel}
              </Link>
            )}
          </nav>
        </div>
      )}
    </>
  )
}
